// PrivateRoute.tsx
import React, { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { buildSigninUrl } from '../../utils/redirectUtils';
import { isActingAsAdmin, isTokenExpired } from '../../utils/authUtils';

interface PrivateRouteProps {
  element: React.ReactElement;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ element }) => {
  const { accessToken, userId } = useContext(AuthContext);
  const location = useLocation();
  const actingAsAdmin = isActingAsAdmin();

  const signinRedirect = (): React.ReactElement => {
    const target = location.pathname + location.search;
    return <Navigate to={buildSigninUrl(target)} replace />;
  };

  const checkTokenExpiration = (): React.ReactElement | null => {
    const storedAccessTokenExpiration = localStorage.getItem('accessTokenExpiration');
    if (isTokenExpired(storedAccessTokenExpiration)) {
      return signinRedirect();
    }
    return null;
  };

  // Check if accessToken and userId exist in the context
  if (accessToken && userId) {
    const tokenExpirationElement = checkTokenExpiration();
    if (tokenExpirationElement) {
      return tokenExpirationElement;
    }
    if (actingAsAdmin) {
      return <Navigate to="/admin" replace />;
    }
    return element;
  }

  // If accessToken and userId are not in the context, try to retrieve them from local storage
  const storedAccessToken = localStorage.getItem('accessToken');
  const storedUserId = localStorage.getItem('userId');

  if (storedAccessToken && storedUserId) {
    const tokenExpirationElement = checkTokenExpiration();
    if (tokenExpirationElement) {
      return tokenExpirationElement;
    }
    if (actingAsAdmin) {
      return <Navigate to="/admin" replace />;
    }
    return element;
  }

  return signinRedirect();
};

export default PrivateRoute;